/**
 * The front door: what the database holds, the biggest systems as a shelf, where help is needed
 * most, and what changed lately.
 */
import { Link } from 'react-router'
import { ArrowRight } from 'lucide-react'
import { SystemShelf, ShelfSkeleton, BookShelf } from '../components/Cards'
import { EditRow } from '../components/EditRow'
import { useQuery } from '../lib/query'
import { num, plural } from '../lib/format'
import { useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import { qs } from '../lib/api'
import { gameParam, useGameScope } from '../lib/gameScope'
import { HOME_HEADLINES, HomeIntro } from './HomeIntro'
import type { BookCard, EditList, Page, Stats, SystemCard } from '../lib/types'

export function Home() {
  useTitle(null)
  const session = useSession()
  const { data: stats } = useQuery<Stats>('/api/v1/stats')
  const scope = useGameScope()
  const game = gameParam(scope)
  const { data: systems } = useQuery<Page<SystemCard>>(`/api/v1/systems${qs({ game, sort: 'books', limit: 12 })}`)
  const { data: gaps } = useQuery<Page<BookCard>>(
    `/api/v1/books${qs({ game, incomplete: 1, sort: 'completeness', limit: 8 })}`,
  )
  const { data: changes } = useQuery<EditList>('/api/v1/edits?status=applied&limit=6')

  return (
    <div className="page home">
      <HomeIntro headline={HOME_HEADLINES[session.wargames ? scope : 'ttrpg']} wargames={session.wargames}>
        {/* Always there, so the text below does not move when the figures arrive. */}
        <p className="stat-line">
          {stats && (
            <>
              <Link to="/systems">{num(stats.systems)} systems</Link>
              <Link to="/books">{num(stats.books)} books</Link>
              {stats.series > 0 && <Link to="/series">{num(stats.series)} series & settings</Link>}
              <Link to="/publishers">{num(stats.publishers)} publishers</Link>
              <Link to="/creators">{num(stats.creators)} creators</Link>
              <Link to="/review" className={stats.pending ? 'is-pending' : undefined}>
                {stats.pending ? `${plural(stats.pending, 'edit')} waiting for review` : 'Review queue is clear'}
              </Link>
            </>
          )}
        </p>
      </HomeIntro>

      <section className="section">
        <div className="section-head">
          <h2 className="section-title">Systems</h2>
          <Link to="/systems" className="section-more">
            All systems <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
        {systems ? <SystemShelf items={systems.items} label="Largest systems" /> : <ShelfSkeleton count={12} />}
      </section>

      <div className="home-split">
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">Gaps worth filling</h2>
            <Link to="/books?incomplete=1&sort=completeness" className="section-more">
              More <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <p className="section-note">
            The least complete books in the catalogue
            {stats ? `. Books average ${stats.book_completeness}% complete` : ''}. The gold frame around each cover
            shows how much is known.
          </p>
          {gaps ? <BookShelf items={gaps.items} label="Least complete books" /> : <ShelfSkeleton count={8} />}
        </section>

        <section className="section">
          <div className="section-head">
            <h2 className="section-title">Recent changes</h2>
            <Link to="/changes" className="section-more">
              All <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
          {changes && changes.items.length === 0 ? (
            <p className="section-note">Nothing yet. The first edit could be yours.</p>
          ) : (
            <ol className="edit-rows is-compact">
              {changes?.items.map((e) => (
                <li key={e.id}>
                  <EditRow edit={e} compact />
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  )
}
